import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Checkbox,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Collapse,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { visuallyHidden } from '@mui/utils';
import type { TableColumn } from '../../types/common.types';

interface DataTableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  total?: number;
  page?: number;
  rowsPerPage?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  selectable?: boolean;
  selectedRows?: string[];
  onSelectionChange?: (selected: string[]) => void;
  getRowId?: (row: T) => string;
  actions?: (row: T) => React.ReactNode;
  emptyMessage?: string;
  emptyStateComponent?: React.ReactNode;
  loading?: boolean;
  getRowSx?: (row: T) => Record<string, any>;
  getCardSx?: (row: T) => Record<string, any>;
}

function DataTable<T>({
  columns,
  data,
  total = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  sortField,
  sortDirection = 'asc',
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  getRowId = (row: T) => (row as { id: string }).id,
  actions,
  emptyMessage = 'Nessun dato disponibile',
  emptyStateComponent,
  loading = false,
  getRowSx,
  getCardSx
}: DataTableProps<T>) {
  const [localSortField, setLocalSortField] = useState<string>(sortField || '');
  const [localSortDirection, setLocalSortDirection] = useState<'asc' | 'desc'>(sortDirection);

  const handleSort = (field: string) => {
    const isAsc = localSortField === field && localSortDirection === 'asc';
    const newDirection = isAsc ? 'desc' : 'asc';

    setLocalSortField(field);
    setLocalSortDirection(newDirection);

    if (onSort) {
      onSort(field, newDirection);
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = data.map(getRowId);
      onSelectionChange?.(allIds);
    } else {
      onSelectionChange?.([]);
    }
  };

  const handleSelectRow = (id: string) => {
    const selectedIndex = selectedRows.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = [...selectedRows, id];
    } else {
      newSelected = selectedRows.filter((rowId) => rowId !== id);
    }

    onSelectionChange?.(newSelected);
  };

  const isSelected = (id: string) => selectedRows.indexOf(id) !== -1;

  // Mobile card component
  const MobileCard = ({ row }: { row: T }) => {
    const [expanded, setExpanded] = useState(false);
    const rowId = getRowId(row);
    const isItemSelected = isSelected(rowId);

    const priorityColumns = columns.filter((col) => col.mobilePriority === 1);
    const detailColumns = columns.filter((col) => col.mobilePriority === 2);
    const hasDetails = detailColumns.length > 0;

    return (
      <Card
        key={rowId}
        sx={{
          mb: 2,
          position: 'relative',
          border: isItemSelected ? '2px solid' : '1px solid',
          borderColor: isItemSelected ? 'primary.main' : 'divider',
          ...(getCardSx ? getCardSx(row) : {})
        }}
      >
        {selectable && (
          <Checkbox
            checked={isItemSelected}
            onChange={() => handleSelectRow(rowId)}
            sx={{ position: 'absolute', top: 8, right: 8 }}
            inputProps={{
              'aria-label': `${isItemSelected ? 'Deseleziona' : 'Seleziona'} elemento`
            }}
          />
        )}
        <CardContent sx={{ pb: 1, pt: selectable ? 5 : 2 }}>
          {priorityColumns.map((column, index) => {
            const value = (row as Record<string, unknown>)[column.id as string];
            return (
              <Box key={String(column.id)} sx={{ mb: index < priorityColumns.length - 1 ? 1.5 : 0 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  {column.label}
                </Typography>
                <Box sx={{ fontSize: '0.875rem', lineHeight: 1.43 }}>
                  {column.format ? column.format(value, row) : String(value || '-')}
                </Box>
              </Box>
            );
          })}

          {hasDetails && (
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Divider sx={{ my: 2 }} />
              {detailColumns.map((column, index) => {
                const value = (row as Record<string, unknown>)[column.id as string];
                return (
                  <Box key={String(column.id)} sx={{ mb: index < detailColumns.length - 1 ? 1.5 : 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      {column.label}
                    </Typography>
                    <Box sx={{ fontSize: '0.875rem', lineHeight: 1.43 }}>
                      {column.format ? column.format(value, row) : String(value || '-')}
                    </Box>
                  </Box>
                );
              })}
            </Collapse>
          )}
        </CardContent>
        <CardActions sx={{ justifyContent: 'space-between', px: 2, pt: 0 }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {actions && actions(row)}
          </Box>
          {hasDetails && (
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              endIcon={
                <ExpandMore
                  sx={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}
                />
              }
            >
              {expanded ? 'Nascondi dettagli' : 'Vedi dettagli'}
            </Button>
          )}
        </CardActions>
      </Card>
    );
  };

  // Detect mobile viewport
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // <900px

  return (
    <Paper elevation={1} sx={{ width: '100%', overflow: 'hidden' }}>
      {isMobile ? (
        // Mobile card layout
        <Box sx={{ p: 2 }}>
          {loading ? (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ py: 4 }}
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              Caricamento...
            </Typography>
          ) : data.length === 0 ? (
            emptyStateComponent || (
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                role="status"
                aria-live="polite"
                sx={{ py: 4 }}
              >
                {emptyMessage}
              </Typography>
            )
          ) : (
            data.map((row) => <MobileCard key={getRowId(row)} row={row} />)
          )}
        </Box>
      ) : (
        // Desktop table layout
        <TableContainer>
        <Table stickyHeader aria-label="Tabella dati con ordinamento e paginazione">
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedRows.length > 0 && selectedRows.length < data.length
                    }
                    checked={data.length > 0 && selectedRows.length === data.length}
                    onChange={handleSelectAll}
                    inputProps={{
                      'aria-label': selectedRows.length > 0
                        ? `${selectedRows.length} ${selectedRows.length === 1 ? 'riga selezionata' : 'righe selezionate'}. Deseleziona tutte`
                        : 'Seleziona tutte le righe'
                    }}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={String(column.id)}
                  align={column.align || 'left'}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.sortable !== false ? (
                    <TableSortLabel
                      active={localSortField === column.id}
                      direction={
                        localSortField === column.id ? localSortDirection : 'asc'
                      }
                      onClick={() => handleSort(String(column.id))}
                    >
                      {column.label}
                      {localSortField === column.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {localSortDirection === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              {actions && <TableCell align="right">Azioni</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0)}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                  >
                    Caricamento...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0)}
                  align="center"
                  sx={{ py: 0 }}
                >
                  {emptyStateComponent || (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      role="status"
                      aria-live="polite"
                      sx={{ py: 4 }}
                    >
                      {emptyMessage}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => {
                const rowId = getRowId(row);
                const isItemSelected = isSelected(rowId);

                return (
                  <TableRow
                    hover
                    key={rowId}
                    selected={isItemSelected}
                    role="row"
                    aria-selected={selectable ? isItemSelected : undefined}
                    sx={{ 
                      '&:last-child td, &:last-child th': { border: 0 },
                      ...(getRowSx ? getRowSx(row) : {})
                    }}
                  >
                    {selectable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onChange={() => handleSelectRow(rowId)}
                          inputProps={{
                            'aria-label': `${isItemSelected ? 'Deseleziona' : 'Seleziona'} riga`
                          }}
                        />
                      </TableCell>
                    )}
                    {columns.map((column) => {
                      const value = (row as Record<string, unknown>)[column.id as string];
                      return (
                        <TableCell key={String(column.id)} align={column.align || 'left'}>
                          {column.format ? column.format(value, row) : String(value || '-')}
                        </TableCell>
                      );
                    })}
                    {actions && (
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          {actions(row)}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}
      {onPageChange && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => onPageChange(newPage)}
          onRowsPerPageChange={(event) =>
            onRowsPerPageChange?.(parseInt(event.target.value, 10))
          }
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} di ${count !== -1 ? count : `piu di ${to}`}`
          }
          slotProps={{
            actions: {
              previousButton: {
                'aria-label': 'Pagina precedente'
              },
              nextButton: {
                'aria-label': 'Pagina successiva'
              }
            }
          }}
        />
      )}
    </Paper>
  );
}

export default DataTable;
